import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';

interface InvitationDetails {
  id: string;
  project_id: string;
  project_name: string;
  role: string;
  inviter_name: string;
  expires_at: string;
  status: string;
}

export default function TeamInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    fetchInvitationDetails();
  }, [token]);

  const fetchInvitationDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('team_project_invitations')
        .select(`
          id,
          project_id,
          role,
          status,
          expires_at,
          created_by,
          team_projects!inner(name)
        `)
        .eq('token', token)
        .single();

      if (error || !data) {
        setError('Invitation not found or expired');
        return;
      }

      if (data.status !== 'pending') {
        setError('This invitation has already been used or expired');
        return;
      }

      if (new Date(data.expires_at) < new Date()) {
        setError('This invitation has expired');
        return;
      }

      // Get inviter profile separately
      const { data: inviterProfile } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', data.created_by)
        .single();

      setInvitation({
        id: data.id,
        project_id: data.project_id,
        project_name: data.team_projects.name,
        role: data.role,
        inviter_name: inviterProfile?.full_name || inviterProfile?.email || 'Someone',
        expires_at: data.expires_at,
        status: data.status
      });
    } catch (err) {
      console.error('Error fetching invitation:', err);
      setError('Failed to load invitation details');
    } finally {
      setLoading(false);
    }
  };

  const acceptMutation = useMutation({
    mutationFn: async () => {
      if (!token) throw new Error('No token provided');
      
      const { data, error } = await supabase.functions.invoke('accept-team-project-invitation', {
        body: { token }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: "Invitation Accepted",
        description: `You've successfully joined "${data.projectName}" as a ${data.role}.`,
      });
      navigate(`/team-projects/${data.projectId}`);
    },
    onError: (error: any) => {
      console.error('Error accepting invitation:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to accept invitation. Please try again.",
        variant: "destructive",
      });
    },
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center">
              <LoadingSpinner />
              <span className="ml-2">Loading invitation...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invitation Error</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <Button 
              onClick={() => navigate('/auth')} 
              className="w-full mt-4"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Sign In Required</CardTitle>
            <CardDescription>
              You need to sign in to accept this team invitation
            </CardDescription>
          </CardHeader>
          <CardContent>
            {invitation && (
              <div className="mb-4 p-4 bg-muted rounded-lg">
                <p className="font-medium">{invitation.project_name}</p>
                <p className="text-sm text-muted-foreground">
                  Invited by {invitation.inviter_name} as {invitation.role}
                </p>
              </div>
            )}
            <div className="space-y-3">
              <Button 
                onClick={() => navigate(`/auth?mode=signin&redirect=/team-invite/${token}`)} 
                className="w-full"
              >
                Sign In to Accept
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate(`/auth?mode=signup&redirect=/team-invite/${token}`)} 
                className="w-full"
              >
                Create Account & Accept
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Team Project Invitation</CardTitle>
          <CardDescription>
            You've been invited to join a team project
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invitation && (
            <>
              <div className="mb-6 p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-lg mb-2">{invitation.project_name}</h3>
                <p className="text-sm text-muted-foreground mb-1">
                  <span className="font-medium">Invited by:</span> {invitation.inviter_name}
                </p>
                <p className="text-sm text-muted-foreground mb-1">
                  <span className="font-medium">Role:</span> {invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1)}
                </p>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium">Expires:</span> {new Date(invitation.expires_at).toLocaleDateString()}
                </p>
              </div>

              <div className="flex gap-3">
                <Button 
                  onClick={() => acceptMutation.mutate()}
                  disabled={acceptMutation.isPending}
                  className="flex-1"
                >
                  {acceptMutation.isPending ? 'Accepting...' : 'Accept Invitation'}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/dashboard')}
                  className="flex-1"
                >
                  Decline
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}